import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button, Typography, Divider } from 'antd';

const { Text, Title } = Typography;

// --- Global Constants ---
const GAME_CANVAS_W = 900;
const GAME_CANVAS_H = 740;
const PREVIEW_W = 280;
const PREVIEW_H = 210;
const BUBBLE_RADIUS = 50;
const POSE_CONFIDENCE_THRESHOLD = 0.5;
const MAX_WRONG_ATTEMPTS = 30;
const BUBBLE_FLOAT_SPEED = 0.7;
const HAND_INDICATOR_RADIUS = 15;
const NUMBER_OF_BUBBLES = 3;

// Question Type Configuration
const QUESTION_TYPES = {
  MATH: 'math',
  COLOR: 'color',
  SHAPE: 'shape',
  WORD_MATCH: 'word_match',
  PATTERN: 'pattern',
  COUNT: 'count',
  BIGGER_SMALLER: 'bigger_smaller',
};

// Color options for color matching
const COLORS = [
  { name: 'Red', value: '#ff4444', display: '#ff4444' },
  { name: 'Blue', value: '#4444ff', display: '#4444ff' },
  { name: 'Green', value: '#44ff44', display: '#44ff44' },
  { name: 'Yellow', value: '#ffff44', display: '#ffff44' },
  { name: 'Purple', value: '#ff44ff', display: '#ff44ff' },
  { name: 'Orange', value: '#ff8844', display: '#ff8844' },
  { name: 'Pink', value: '#ffaadd', display: '#ffaadd' },
  { name: 'Cyan', value: '#44ffff', display: '#44ffff' },
];

// Shape options
const SHAPES = ['Circle', 'Square', 'Triangle', 'Star', 'Heart'];

// Word pairs for matching
const WORD_PAIRS = [
  { category: 'Animals', words: ['Dog', 'Cat', 'Bird', 'Fish', 'Lion', 'Bear'] },
  { category: 'Fruits', words: ['Apple', 'Banana', 'Orange', 'Grape', 'Mango', 'Peach'] },
  { category: 'Colors', words: ['Red', 'Blue', 'Green', 'Yellow', 'Purple', 'Orange'] },
  { category: 'Numbers', words: ['One', 'Two', 'Three', 'Four', 'Five', 'Six'] },
];

// MediaPipe Hand Landmarks
const WRIST = 0;
const THUMB_TIP = 4;
const INDEX_FINGER_TIP = 8;
const INDEX_FINGER_PIP = 6; // Proximal interphalangeal joint
const MIDDLE_FINGER_TIP = 12;
const MIDDLE_FINGER_PIP = 10;
const RING_FINGER_TIP = 16;
const RING_FINGER_PIP = 14;
const PINKY_TIP = 20;
const PINKY_PIP = 18;

// Utility function to calculate distance between two points
const getDistance = (p1, p2) => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

// Utility function to detect if hand is making a fist
const isFistClosed = (landmarks) => {
  // Instead of checking if fingertips are close to PIP joints,
  // check if fingertips are BELOW (higher Y value) their MCP joints (knuckles)
  // This detects when fingers are curled down naturally
  
  const wrist = landmarks[WRIST];
  const indexTip = landmarks[INDEX_FINGER_TIP];
  const middleTip = landmarks[MIDDLE_FINGER_TIP];
  const ringTip = landmarks[RING_FINGER_TIP];
  const pinkyTip = landmarks[PINKY_TIP];
  
  // Get the base knuckles (MCP joints)
  const indexMCP = landmarks[5];  // Index finger MCP
  const middleMCP = landmarks[9]; // Middle finger MCP
  const ringMCP = landmarks[13];  // Ring finger MCP
  const pinkyMCP = landmarks[17]; // Pinky MCP
  
  // Check if fingertips are curled down (Y value greater than knuckle Y)
  // In normalized coordinates, Y increases downward
  const indexCurled = indexTip.y > indexMCP.y;
  const middleCurled = middleTip.y > middleMCP.y;
  const ringCurled = ringTip.y > ringMCP.y;
  const pinkyCurled = pinkyTip.y > pinkyMCP.y;
  
  // At least 3 fingers should be curled down to count as a fist
  const curledCount = [indexCurled, middleCurled, ringCurled, pinkyCurled].filter(Boolean).length;
  return curledCount >= 3;
};

// Utility function to draw shapes
const drawShape = (ctx, shape, x, y, size) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = '#fff';
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 3;
  
  switch (shape) {
    case 'Circle':
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.4, 0, Math.PI * 2);
      ctx.stroke();
      break;
      
    case 'Square':
      ctx.strokeRect(-size * 0.3, -size * 0.3, size * 0.6, size * 0.6);
      break;
      
    case 'Triangle':
      ctx.beginPath();
      ctx.moveTo(0, -size * 0.35);
      ctx.lineTo(-size * 0.35, size * 0.25);
      ctx.lineTo(size * 0.35, size * 0.25);
      ctx.closePath();
      ctx.stroke();
      break;
      
    case 'Star':
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
        const radius = i % 2 === 0 ? size * 0.4 : size * 0.18;
        const px = Math.cos(angle) * radius;
        const py = Math.sin(angle) * radius;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
      break;
      
    case 'Heart':
      ctx.beginPath();
      const topY = -size * 0.2;
      ctx.moveTo(0, size * 0.3);
      ctx.bezierCurveTo(-size * 0.3, topY - size * 0.15, -size * 0.4, topY + size * 0.1, 0, topY + size * 0.35);
      ctx.bezierCurveTo(size * 0.4, topY + size * 0.1, size * 0.3, topY - size * 0.15, 0, size * 0.3);
      ctx.stroke();
      break;
  }
  
  ctx.restore();
};

// Utility function for shuffling an array
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// Utility function to generate unique distractors close to the correct answer
const generateDistractors = (correctAnswer) => {
  const distractors = new Set();
  
  while (distractors.size < NUMBER_OF_BUBBLES - 1) {
    let diff = Math.floor(Math.random() * 5) + 1;
    let sign = Math.random() < 0.5 ? 1 : -1;
    let distractor = correctAnswer + (diff * sign);

    if (distractor >= 0 && distractor !== correctAnswer && !distractors.has(distractor)) {
      distractors.add(distractor);
    }
  }
  return Array.from(distractors);
};

// --- Main Component ---
export default function MathBubblePop() {
  const videoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const handsEstimatorRef = useRef(null);
  const cameraRef = useRef(null);

  const [gameState, setGameState] = useState('ready');
  const [score, setScore] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState(0);
  const [status, setStatus] = useState("Ready – Click Start to begin.");
  const [currentQuestionText, setCurrentQuestionText] = useState(null);

  const gameDataRef = useRef({
    bubbles: [],
    handCenter: { x: 0, y: 0, visible: false },
    isFist: false,
    wasFist: false, // Track previous state to detect fist action
    correctAnswer: null,
    isQuestionActive: false,
    popEffect: null,
    particles: [],
  });

  // --- Question Generation & Bubble Setup ---
  const generateQuestion = useCallback(() => {
    // Randomly select a question type
    const questionTypes = Object.values(QUESTION_TYPES);
    const selectedType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
    
    let questionText = '';
    let correctAnswer = null;
    let options = [];
    let bubbleColors = null; // For color-based questions
    let bubbleShapes = null; // For shape-based questions

    switch (selectedType) {
      case QUESTION_TYPES.MATH: {
        // Original math questions
        let num1, num2, operator;
        const operationType = Math.floor(Math.random() * 4);

        if (operationType === 0) {
          operator = '+';
          num1 = Math.floor(Math.random() * 10) + 1;
          num2 = Math.floor(Math.random() * 10) + 1;
          correctAnswer = num1 + num2;
        } else if (operationType === 1) {
          operator = '-';
          num1 = Math.floor(Math.random() * 15) + 5;
          num2 = Math.floor(Math.random() * num1);
          correctAnswer = num1 - num2;
        } else if (operationType === 2) {
          operator = '×';
          num1 = Math.floor(Math.random() * 10) + 1;
          num2 = Math.floor(Math.random() * 10) + 1;
          correctAnswer = num1 * num2;
        } else {
          operator = '÷';
          num2 = Math.floor(Math.random() * 10) + 1;
          correctAnswer = Math.floor(Math.random() * 10) + 1;
          num1 = num2 * correctAnswer;
        }

        questionText = `${num1} ${operator} ${num2}`;
        const distractors = generateDistractors(correctAnswer);
        options = [correctAnswer, ...distractors];
        break;
      }

      case QUESTION_TYPES.COLOR: {
        // Color matching - "Pop the RED bubble"
        const correctColor = COLORS[Math.floor(Math.random() * COLORS.length)];
        correctAnswer = correctColor.name;
        questionText = `Pop the ${correctColor.name.toUpperCase()} bubble`;
        
        // Generate distractor colors
        let distractorColors = COLORS.filter(c => c.name !== correctColor.name);
        shuffleArray(distractorColors);
        const selectedColors = [correctColor, ...distractorColors.slice(0, NUMBER_OF_BUBBLES - 1)];
        shuffleArray(selectedColors);
        
        options = selectedColors.map(c => c.name);
        bubbleColors = selectedColors.map(c => c.display);
        break;
      }

      case QUESTION_TYPES.SHAPE: {
        // Shape matching - "Find the STAR"
        const correctShape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
        correctAnswer = correctShape;
        questionText = `Find the ${correctShape.toUpperCase()}`;
        
        // Generate distractor shapes
        let distractorShapes = SHAPES.filter(s => s !== correctShape);
        shuffleArray(distractorShapes);
        const selectedShapes = [correctShape, ...distractorShapes.slice(0, NUMBER_OF_BUBBLES - 1)];
        shuffleArray(selectedShapes);
        
        options = selectedShapes;
        bubbleShapes = selectedShapes;
        break;
      }

      case QUESTION_TYPES.WORD_MATCH: {
        // Word category matching - "Which is a FRUIT?"
        const category = WORD_PAIRS[Math.floor(Math.random() * WORD_PAIRS.length)];
        const correctWord = category.words[Math.floor(Math.random() * category.words.length)];
        correctAnswer = correctWord;
        questionText = `Which is a ${category.category.slice(0, -1).toUpperCase()}?`;
        
        // Generate distractors from other categories
        let distractors = [];
        const otherCategories = WORD_PAIRS.filter(c => c.category !== category.category);
        otherCategories.forEach(cat => {
          distractors.push(...cat.words);
        });
        shuffleArray(distractors);
        
        options = [correctWord, ...distractors.slice(0, NUMBER_OF_BUBBLES - 1)];
        shuffleArray(options);
        break;
      }

      case QUESTION_TYPES.PATTERN: {
        // Pattern completion - "What comes next? 2, 4, 6, ?"
        const start = Math.floor(Math.random() * 5) + 1;
        const step = Math.floor(Math.random() * 3) + 2;
        const sequence = [start, start + step, start + step * 2];
        correctAnswer = start + step * 3;
        questionText = `Pattern: ${sequence.join(', ')}, ?`;
        
        const distractors = generateDistractors(correctAnswer);
        options = [correctAnswer, ...distractors];
        break;
      }

      case QUESTION_TYPES.COUNT: {
        // Counting - "How many dots? ●●●●"
        const count = Math.floor(Math.random() * 7) + 3;
        correctAnswer = count;
        questionText = `Count: ${'●'.repeat(count)}`;
        
        const distractors = generateDistractors(correctAnswer);
        options = [correctAnswer, ...distractors];
        break;
      }

      case QUESTION_TYPES.BIGGER_SMALLER: {
        // Comparison - "Which is BIGGER?"
        const num1 = Math.floor(Math.random() * 50) + 10;
        const num2 = Math.floor(Math.random() * 50) + 10;
        const num3 = Math.floor(Math.random() * 50) + 10;
        
        const numbers = [num1, num2, num3];
        const isBigger = Math.random() < 0.5;
        
        if (isBigger) {
          correctAnswer = Math.max(...numbers);
          questionText = 'Which is BIGGER?';
        } else {
          correctAnswer = Math.min(...numbers);
          questionText = 'Which is SMALLER?';
        }
        
        options = numbers;
        break;
      }

      default:
        // Fallback to math
        correctAnswer = 5;
        options = [5, 6, 7];
        questionText = '2 + 3';
    }

    shuffleArray(options);

    const bubbleDiameter = BUBBLE_RADIUS * 2;
    const totalSpaceAvailable = GAME_CANVAS_W - (NUMBER_OF_BUBBLES * bubbleDiameter);
    const xSpacing = totalSpaceAvailable / (NUMBER_OF_BUBBLES + 1);

    gameDataRef.current.correctAnswer = correctAnswer;
    gameDataRef.current.bubbles = options.map((value, index) => ({
      x: xSpacing + BUBBLE_RADIUS + (index * (xSpacing + bubbleDiameter)),
      y: GAME_CANVAS_H - BUBBLE_RADIUS - 50,
      radius: BUBBLE_RADIUS,
      speed: BUBBLE_FLOAT_SPEED + (Math.random() * 0.2),
      value,
      isCorrect: value === correctAnswer,
      state: 'floating',
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 0.02 + Math.random() * 0.02,
      color: bubbleColors ? bubbleColors[index] : null,
      shape: bubbleShapes ? bubbleShapes[index] : null,
      questionType: selectedType,
    }));
    
    console.log('Generated bubbles:', gameDataRef.current.bubbles);
    
    setCurrentQuestionText(questionText);
    gameDataRef.current.isQuestionActive = true;
    gameDataRef.current.popEffect = null;
    gameDataRef.current.particles = [];
    setStatus(`Move hand over the answer and CURL your fingers to pop!`);
  }, []);

  // --- Create Pop Effect Particles ---
  const createPopEffect = (x, y, isCorrect) => {
    const color = isCorrect ? '#4ade80' : '#ff4444';
    gameDataRef.current.popEffect = { x, y, isCorrect, duration: 30 };
    
    for (let i = 0; i < 20; i++) {
      const angle = (Math.PI * 2 * i) / 20;
      const speed = 3 + Math.random() * 3;
      gameDataRef.current.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 3 + Math.random() * 5,
        color,
        life: 1,
      });
    }
  };

  // --- MediaPipe Hands Setup ---
  const setupMediaPipe = useCallback(() => {
    if (!window.Hands || !window.Camera) {
      console.error("MediaPipe Hands libraries not loaded.");
      setStatus("Error: MediaPipe not loaded. Please refresh.");
      return;
    }

    const hands = new window.Hands({
      locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4/${file}`,
    });
    
    hands.setOptions({
      maxNumHands: 1,
      modelComplexity: 1,
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });

    hands.onResults((results) => {
      if (!canvasRef.current || !handsEstimatorRef.current) return;
  
      const gameData = gameDataRef.current;
      
      if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
        const handLandmarks = results.multiHandLandmarks[0];
        
        // Calculate hand center (average of all landmarks for stability)
        let sumX = 0, sumY = 0;
        for (let i = 0; i < handLandmarks.length; i++) {
          sumX += handLandmarks[i].x;
          sumY += handLandmarks[i].y;
        }
        const centerX = sumX / handLandmarks.length;
        const centerY = sumY / handLandmarks.length;
        
        // Mirror X coordinate for better UX
        gameData.handCenter = {
          x: (1 - centerX) * GAME_CANVAS_W,
          y: centerY * GAME_CANVAS_H,
          visible: true,
        };
        
        // Detect if making a fist
        gameData.isFist = isFistClosed(handLandmarks);
        
      } else {
        gameData.handCenter.visible = false;
        gameData.isFist = false;
      }
    });
    
    handsEstimatorRef.current = hands;
    
    navigator.mediaDevices.getUserMedia({ 
      video: { 
        width: { ideal: 640 }, 
        height: { ideal: 480 },
        facingMode: 'user'
      } 
    })
    .then((stream) => {
      if (!videoRef.current || !previewVideoRef.current) return;
      
      videoRef.current.srcObject = stream;
      previewVideoRef.current.srcObject = stream;
      
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play();
        previewVideoRef.current.play();
        
        const camera = new window.Camera(videoRef.current, {
          onFrame: async () => {
            if (handsEstimatorRef.current && videoRef.current) {
              try {
                await handsEstimatorRef.current.send({ image: videoRef.current });
              } catch (e) {
                // Ignore errors from closed estimator
              }
            }
          },
          width: 640, 
          height: 480,
        });
        
        cameraRef.current = camera;
        camera.start();
        setStatus("Camera ready! Click Start Game.");
      };
    })
    .catch((err) => {
      console.error("Camera access error:", err);
      setStatus("Error: Camera access denied. Please allow camera.");
    });
  }, []);

  // --- Game Flow Handler ---
  const handleAnswerResult = useCallback((isCorrect, bubble) => {
    gameDataRef.current.isQuestionActive = false;
    
    createPopEffect(bubble.x, bubble.y, isCorrect);
    
    if (isCorrect) {
      setScore(s => s + 1);
      setStatus("✅ Correct! Next question...");
      setTimeout(() => generateQuestion(), 1000);
    } else {
      setStatus("❌ Wrong! Next question...");
      setWrongAnswers(w => {
        const newWrong = w + 1;
        if (newWrong >= MAX_WRONG_ATTEMPTS) {
          setGameState('finished');
        } else {
          setTimeout(() => generateQuestion(), 1000);
        }
        return newWrong;
      });
    }
  }, [generateQuestion]);

  const handleMissedAnswer = useCallback(() => {
    gameDataRef.current.isQuestionActive = false;
    setStatus("⏱️ Missed! Bubble escaped...");
    
    setWrongAnswers(w => {
      const newWrong = w + 1;
      if (newWrong >= MAX_WRONG_ATTEMPTS) {
        setGameState('finished');
      } else {
        setTimeout(() => generateQuestion(), 1000);
      }
      return newWrong;
    });
  }, [generateQuestion]);

  // --- Game Loop Logic ---
  const gameLoopLogic = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) {
      console.log('No canvas context');
      return;
    }

    const { bubbles, particles, popEffect, indexFingerTip } = gameDataRef.current;
    
    // Clear canvas and draw gradient background
    ctx.clearRect(0, 0, GAME_CANVAS_W, GAME_CANVAS_H);
    const gradient = ctx.createLinearGradient(0, 0, 0, GAME_CANVAS_H);
    gradient.addColorStop(0, '#1a1a2e');
    gradient.addColorStop(1, '#16213e');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, GAME_CANVAS_W, GAME_CANVAS_H);

    // Update and draw particles
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.2;
      p.life -= 0.02;
      
      if (p.life <= 0) {
        particles.splice(i, 1);
        continue;
      }
      
      ctx.globalAlpha = p.life;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Draw pop effect
    if (popEffect && popEffect.duration > 0) {
      const alpha = popEffect.duration / 30;
      ctx.beginPath();
      ctx.arc(popEffect.x, popEffect.y, BUBBLE_RADIUS * 2 * (1 - alpha * 0.5), 0, Math.PI * 2);
      ctx.fillStyle = popEffect.isCorrect 
        ? `rgba(74, 222, 128, ${alpha * 0.5})` 
        : `rgba(255, 68, 68, ${alpha * 0.5})`;
      ctx.fill();
      popEffect.duration--;
    }

    let correctBubbleMissed = false;

    // --- COLLISION CHECK (Fist Punch Detection) ---
    // Only pop bubble when transitioning from open hand to fist (the "punch" action)
    const { handCenter, isFist, wasFist } = gameDataRef.current;
    
    if (isFist && !wasFist && gameDataRef.current.isQuestionActive && handCenter.visible) {
      // This is a punch action! (just made a fist)
      const punchX = handCenter.x;
      const punchY = handCenter.y;
      
      for (const bubble of bubbles) {
        if (bubble.state !== 'floating') continue;

        const dx = punchX - bubble.x;
        const dy = punchY - bubble.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Pop the bubble if hand is inside it when fist is made
        if (distance < bubble.radius + HAND_INDICATOR_RADIUS) {
          bubble.state = 'popped';
          handleAnswerResult(bubble.isCorrect, bubble);
          console.log('Bubble popped with punch!');
          break;
        }
      }
    }
    
    // Update previous fist state for next frame
    gameDataRef.current.wasFist = isFist;

    // Move and draw bubbles
    for (const bubble of bubbles) {
      if (bubble.state === 'floating') {
        bubble.y -= bubble.speed;
        bubble.wobble += bubble.wobbleSpeed;

        const wobbleX = Math.sin(bubble.wobble) * 10;

        // Check if bubble escaped
        if (bubble.y < -bubble.radius && gameDataRef.current.isQuestionActive) {
          bubble.state = 'missed';
          if (bubble.isCorrect) {
            correctBubbleMissed = true;
          }
        }

        // Draw bubble
        ctx.save();
        ctx.translate(bubble.x + wobbleX, bubble.y);
        
        // Determine bubble color based on question type
        let bubbleGradient;
        if (bubble.color) {
          // Color-based question - use the specified color
          bubbleGradient = ctx.createRadialGradient(
            -bubble.radius * 0.3, -bubble.radius * 0.3, 0, 0, 0, bubble.radius
          );
          bubbleGradient.addColorStop(0, bubble.color);
          bubbleGradient.addColorStop(1, bubble.color);
        } else {
          // Default gradient
          bubbleGradient = ctx.createRadialGradient(
            -bubble.radius * 0.3, -bubble.radius * 0.3, 0, 0, 0, bubble.radius
          );
          bubbleGradient.addColorStop(0, bubble.isCorrect ? '#60a5fa' : '#a78bfa');
          bubbleGradient.addColorStop(1, bubble.isCorrect ? '#3b82f6' : '#7c3aed');
        }

        ctx.beginPath();
        ctx.arc(0, 0, bubble.radius, 0, Math.PI * 2);
        ctx.fillStyle = bubbleGradient;
        ctx.fill();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // Draw bubble shine effect
        ctx.beginPath();
        ctx.arc(-bubble.radius * 0.3, -bubble.radius * 0.3, bubble.radius * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.fill();
        
        // Draw content based on question type
        if (bubble.shape) {
          // Draw shape
          drawShape(ctx, bubble.shape, 0, 0, bubble.radius);
        } else {
          // Draw text/number
          ctx.fillStyle = '#fff';
          const fontSize = bubble.value.toString().length > 2 ? 24 : 32;
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 4;
          ctx.fillText(bubble.value, 0, 0);
          ctx.shadowBlur = 0;
        }
        
        ctx.restore();
      }
    }

    // Handle missed correct bubble
    if (correctBubbleMissed) {
      handleMissedAnswer();
    }

    // Draw hand indicator
    if (handCenter.visible) {
      const color = isFist ? 'rgba(255, 68, 68, 0.8)' : 'rgba(255, 215, 0, 0.6)';
      const strokeColor = isFist ? '#ff4444' : '#FFD700';
      const radius = isFist ? HAND_INDICATOR_RADIUS + 5 : HAND_INDICATOR_RADIUS;
      
      ctx.beginPath();
      ctx.arc(handCenter.x, handCenter.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Draw outer ring
      ctx.beginPath();
      ctx.arc(handCenter.x, handCenter.y, radius + 5, 0, Math.PI * 2);
      ctx.strokeStyle = isFist ? 'rgba(255, 68, 68, 0.3)' : 'rgba(255, 215, 0, 0.3)';
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Draw fist indicator text
      if (isFist) {
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('👊', handCenter.x, handCenter.y);
      }
    }
    
    // Draw game over overlay
    if (gameState === 'finished') {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.fillRect(0, 0, GAME_CANVAS_W, GAME_CANVAS_H);

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('GAME OVER!', GAME_CANVAS_W / 2, GAME_CANVAS_H / 2 - 50);

      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = '#4ade80';
      ctx.fillText(`Final Score: ${score}`, GAME_CANVAS_W / 2, GAME_CANVAS_H / 2 + 10);

      ctx.font = '20px Arial';
      ctx.fillStyle = '#fff';
      ctx.fillText('Click "Play Again" to restart', GAME_CANVAS_W / 2, GAME_CANVAS_H / 2 + 60);
    }
  }, [gameState, handleAnswerResult, handleMissedAnswer, score]);

  // --- Game Loop (requestAnimationFrame) ---
  useEffect(() => {
    if (gameState === 'playing' || gameState === 'finished') {
      const animate = () => {
        gameLoopLogic();
        rafRef.current = requestAnimationFrame(animate);
      };
      rafRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [gameState, gameLoopLogic]);

  // --- Game Control ---
  const handleStartGame = () => {
    setScore(0);
    setWrongAnswers(0);
    setGameState('playing');
    
    gameDataRef.current.isQuestionActive = false;
    gameDataRef.current.particles = [];
    gameDataRef.current.popEffect = null;
    gameDataRef.current.handCenter = { x: 0, y: 0, visible: false };
    gameDataRef.current.isFist = false;
    gameDataRef.current.wasFist = false;
    
    generateQuestion();
  };

  // --- Effects ---
  useEffect(() => {
    let mounted = true;
    
    const handsScript = document.createElement('script');
    handsScript.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4/hands.js';
    handsScript.onload = () => {
      if (!mounted) return;
      const cameraScript = document.createElement('script');
      cameraScript.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils@0.3/camera_utils.js';
      cameraScript.onload = () => {
        if (mounted) setupMediaPipe();
      };
      document.body.appendChild(cameraScript);
    };
    document.body.appendChild(handsScript);
    
    return () => {
      mounted = false;
      
      if (cameraRef.current) {
        try {
          cameraRef.current.stop();
        } catch (e) {
          // Already stopped
        }
        cameraRef.current = null;
      }
      
      if (videoRef.current?.srcObject) {
        const tracks = videoRef.current.srcObject.getTracks();
        tracks.forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
      
      if (previewVideoRef.current?.srcObject) {
        previewVideoRef.current.srcObject = null;
      }
      
      if (handsEstimatorRef.current) {
        try {
          handsEstimatorRef.current.close();
        } catch (e) {
          // Already closed
        }
        handsEstimatorRef.current = null;
      }
    };
  }, [setupMediaPipe]);

  // --- UI Layout ---
  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: '#16213e',
      color: '#fff',
      fontFamily: 'Arial, sans-serif'
    }}>
      
      <video ref={videoRef} style={{ display: 'none' }} playsInline muted />

      {/* LEFT PANEL */}
      <div
        style={{
          width: 320,
          background: '#1f1f1f',
          padding: 16,
          boxShadow: '4px 0 10px rgba(0,0,0,0.5)'
        }}
      >
        <Title level={3} style={{ color: '#4ade80', marginTop: 0, marginBottom: 16 }}>
          🫧 Brain Bubble Pop
        </Title>

        <Text style={{
          fontSize: 14,
          fontWeight: 'bold',
          color: '#e6eef8',
          display: 'block',
          marginBottom: 16
        }}>
          {status}
        </Text>

        <Divider style={{ margin: '16px 0', borderColor: 'rgba(255,255,255,0.2)' }} />

        <Button
          onClick={handleStartGame}
          disabled={gameState === 'playing'}
          type="primary"
          block
          size="large"
          style={{
            fontWeight: 'bold',
            background: gameState === 'playing' ? '#555' : '#4ade80',
            borderColor: gameState === 'playing' ? '#555' : '#4ade80',
            marginBottom: 16
          }}
        >
          {gameState === 'ready' ? 'START GAME' : gameState === 'playing' ? 'PLAYING...' : 'PLAY AGAIN'}
        </Button>

        <Divider style={{ margin: '16px 0', borderColor: 'rgba(255,255,255,0.2)' }} />

        <div style={{ marginBottom: 16 }}>
          <Text style={{
            color: '#4ade80',
            fontSize: 24,
            fontWeight: 'bold',
            display: 'block',
          }}>
            ⭐ Score: {score}
          </Text>
          
          <Text style={{
            color: '#fbbf24',
            fontSize: 20,
            fontWeight: 'bold',
            display: 'block',
            marginTop: 8
          }}>
            ❌ Misses: {wrongAnswers}/{MAX_WRONG_ATTEMPTS}
          </Text>

          <div style={{ marginTop: 12 }}>
            {Array(MAX_WRONG_ATTEMPTS).fill(0).map((_, i) => (
              <span key={i} style={{ fontSize: 24, marginRight: 8 }}>
                {i < wrongAnswers ? "❌" : "❤️"}
              </span>
            ))}
          </div>
        </div>

        <Divider style={{ margin: '16px 0', borderColor: 'rgba(255,255,255,0.2)' }} />

        <Text style={{ color: '#999', fontSize: 12, display: 'block', marginBottom: 8 }}>
          Camera Preview (Mirrored):
        </Text>
        
        <div style={{ 
          width: PREVIEW_W, 
          height: PREVIEW_H, 
          border: '2px solid #444',
          borderRadius: '8px',
          overflow: 'hidden',
          background: '#000'
        }}>
          <video
            ref={previewVideoRef}
            style={{
              transform: 'scaleX(-1)',
              width: '100%',
              height: '100%',
            }}
            playsInline
            muted
          />
        </div>

        <Divider style={{ margin: '16px 0', borderColor: 'rgba(255,255,255,0.2)' }} />

        <div style={{ color: '#bfe7ff', fontSize: 12, lineHeight: 1.6 }}>
          <Text style={{ color: '#bfe7ff', display: 'block', fontWeight: 'bold', marginBottom: 8 }}>
            How to Play:
          </Text>
          <Text style={{ color: '#bfe7ff', display: 'block', marginBottom: 4 }}>
            • Move your <strong>open hand</strong> over a bubble
          </Text>
          <Text style={{ color: '#bfe7ff', display: 'block', marginBottom: 4 }}>
            • <span style={{color: '#FFD700'}}>GOLD circle</span> = hand open (ready)
          </Text>
          <Text style={{ color: '#bfe7ff', display: 'block', marginBottom: 4 }}>
            • <span style={{color: '#ff4444'}}>RED circle</span> = fingers curled (punching!)
          </Text>
          <Text style={{ color: '#bfe7ff', display: 'block', marginBottom: 4 }}>
            • <strong>CURL your fingers</strong> to pop the correct bubble!
          </Text>
          <Text style={{ color: '#bfe7ff', display: 'block', marginTop: 12, fontWeight: 'bold' }}>
            Question Types:
          </Text>
          <Text style={{ color: '#bfe7ff', display: 'block', fontSize: 11, marginBottom: 2 }}>
            Math • Colors • Shapes • Words • Patterns • Counting
          </Text>
        </div>
      </div>

      {/* RIGHT PANEL - GAME AREA */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: 20
      }}>
        
        {currentQuestionText && gameState === 'playing' && (
          <h1 style={{ 
            color: '#FFD700', 
            fontSize: '4em', 
            margin: '0 0 30px 0',
            textShadow: '0 0 20px rgba(255,215,0,0.6)',
            fontWeight: 'bold'
          }}>
            {currentQuestionText} = ?
          </h1>
        )}

        <div style={{ 
          position: 'relative', 
          width: GAME_CANVAS_W, 
          height: GAME_CANVAS_H,
          boxShadow: '0 0 30px rgba(0,0,0,0.8)',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <canvas
            ref={canvasRef}
            width={GAME_CANVAS_W}
            height={GAME_CANVAS_H}
            style={{
              borderRadius: '12px',
            }}
          />
        </div>
      </div>
    </div>
  );
}